import React, { useState, useEffect, useRef } from 'react';
import { useInfiniteScroll } from '../hook/useInfiniteScroll';

interface DropdownSearchProps<T> {
    refKey: string;
    refs: { [key: string]: React.RefObject<HTMLDivElement> };
    openStates: { [key: string]: boolean };
    open: (key: string) => void;
    close: (key: string) => void;
    data: T[];
    labelExtractor: (item: T) => string;
    value: string;
    setValue: (v: string) => void;
    onSelect: (item: T) => void;
    onSearch?: (v: string) => void;
    placeholder?: string;
    /** Accessible name for the search input when no <label> wraps it. */
    ariaLabel?: string;
    error?: string;
    tabIndex?: number;
    onLoadMore?: () => void;
    hasMore?: boolean;
    loading?: boolean;
    borderNone?: boolean;
    inItem?: boolean;
    disabled?: boolean;
}

export function DropdownSearch<T>({
    refKey,
    refs,
    openStates,
    open,
    close,
    data = [],
    labelExtractor,
    value,
    setValue,
    onSelect,
    onSearch,
    placeholder,
    ariaLabel,
    error,
    tabIndex = 0,
    onLoadMore,
    borderNone = false,
    hasMore = false,
    loading = false,
    inItem = false,
    disabled = false
}: DropdownSearchProps<T>) {
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const dropdownScrollRef = useRef<HTMLDivElement>(null);
    const loaderElementRef = useRef<HTMLDivElement | null>(null);
    const [filtered, setFiltered] = useState<T[]>(data);
    const previousDataLengthRef = useRef<number>(data.length);
    const isLoadingMoreRef = useRef<boolean>(false);
    const selectedItemRef = useRef<T | null>(null);
    const hasUserInteractedRef = useRef<boolean>(false);

    const handleBlur = () => {
        close(refKey);
    };

    useEffect(() => {
        // Chỉ scroll vào item khi KHÔNG đang load more
        if (highlightedIndex !== -1 && itemRefs.current[highlightedIndex] && !isLoadingMoreRef.current) {
            itemRefs.current[highlightedIndex]?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });

            const isLastItem = highlightedIndex === filtered.length - 1;
            if (isLastItem && hasMore && !loading && onLoadMore) {
                isLoadingMoreRef.current = true;
                setTimeout(() => {
                    if (loaderElementRef.current && dropdownScrollRef.current) {
                        const loaderRect = loaderElementRef.current.getBoundingClientRect();
                        const dropdownRect = dropdownScrollRef.current.getBoundingClientRect();
                        if (loaderRect.top < dropdownRect.bottom) {
                            onLoadMore();
                        }
                    }
                }, 100);
            }
        }
    }, [highlightedIndex, hasMore, loading, onLoadMore, filtered.length]);

    const handleKeyDown = (e: any) => {
        if (disabled) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.min(filtered.length - 1, prev + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(0, prev - 1));
        } else if (e.key === 'Tab' && openStates[refKey] && highlightedIndex !== -1) {
            e.preventDefault();
            if (filtered[highlightedIndex]) {
                const selectedItem = filtered[highlightedIndex];
                selectedItemRef.current = selectedItem;
                hasUserInteractedRef.current = false;
                onSelect(selectedItem);
                setValue(labelExtractor(selectedItem));
                close(refKey);
            }
        } else if (e.key === 'Enter' && openStates[refKey]) {
            e.preventDefault();
            e.stopPropagation();
            if (highlightedIndex !== -1 && filtered[highlightedIndex]) {
                const selectedItem = filtered[highlightedIndex];
                selectedItemRef.current = selectedItem;
                hasUserInteractedRef.current = false;
                onSelect(selectedItem);
                setValue(labelExtractor(selectedItem));
                close(refKey);
            } else if (onSearch) {
                onSearch(value);
            }
            return;
        }
    };

    const handleLoadMore = () => {
        if (openStates[refKey] && onLoadMore) {
            onLoadMore();
        }
    };

    const { setLoaderRef } = useInfiniteScroll({
        hasMore: openStates[refKey] && hasMore,
        loading,
        onLoadMore: handleLoadMore,
        scrollRootRef: dropdownScrollRef,
    });

    useEffect(() => {
        if (openStates[refKey]) {
            setHighlightedIndex(-1);
        }
    }, [openStates, refKey]);

    useEffect(() => {
        if (value && !selectedItemRef.current) {
            const matchedItem = data.find(item => labelExtractor(item) === value);
            if (matchedItem) {
                selectedItemRef.current = matchedItem;
            }
        }
    }, [value, data, labelExtractor]);

    useEffect(() => {
        if (openStates[refKey]) {
            setHighlightedIndex(-1);
            if (value) {
                const matchedItem = data.find(item => labelExtractor(item) === value);
                if (matchedItem) {
                    selectedItemRef.current = matchedItem;
                }
            }
        }
    }, [openStates, refKey, value, data, labelExtractor]);

    useEffect(() => {
        // Khi dropdown đang mở và user đang tương tác
        if (openStates[refKey] && hasUserInteractedRef.current) {
            if (!value) {
                setFiltered(data);
            } else {
                const searchLower = value.toLowerCase();
                const filteredData = data.filter((item) => {
                    const label = labelExtractor(item).toLowerCase();
                    return label.includes(searchLower);
                });
                setFiltered(filteredData);
            }
        } else {
            // Dropdown đóng hoặc chưa có tương tác từ user (edit mode)
            setFiltered(data);
        }

        if (data.length > previousDataLengthRef.current) {
            isLoadingMoreRef.current = false;
        }
        previousDataLengthRef.current = data.length;
    }, [data, value, labelExtractor, openStates, refKey]);

    useEffect(() => {
        if (selectedItemRef.current && labelExtractor(selectedItemRef.current) !== value) {
            selectedItemRef.current = null;
        }
    }, [value, labelExtractor]);

    return (
        <>
            <div className="relative w-full h-full" ref={refs[refKey]}>
                {/* <div className="flex items-center justify-center absolute" style={{ width: '24px', height: '24px', top: '50%', transform: 'translateY(-50%)', right: '8px' }}>
                    <DropdownIcon />
                </div> */}
                <input
                    tabIndex={tabIndex}
                    type="text"
                    disabled={disabled}
                    value={value}
                    onChange={(e) => {
                        if (!disabled) {
                            hasUserInteractedRef.current = true;
                            setValue(e.target.value);
                        }
                    }}
                    onFocus={() => !disabled && open(refKey)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    aria-label={ariaLabel}
                    style={{
                        color: "#4A5353",
                        fontSize: "14px",
                        fontWeight: 500,
                        pointerEvents: disabled ? 'none' : 'all',
                        cursor: disabled ? 'not-allowed' : 'text',
                        opacity: disabled ? 0.6 : 1
                    }}
                    className={`h-full font-inter placeholder:text-placeholder ${borderNone === false
                        ? "border border-[#EBEBED] p-3"
                        : "border-none w-full"
                        } rounded-lg outline-none focus:outline-none bg-transparent w-full`}
                />


                {openStates[refKey] && !disabled && (
                    <div
                        ref={dropdownScrollRef}
                        className="shadow-dropdown absolute z-10 w-full mt-1 bg-white border border-[#EBEBED] rounded-md shadow-lg max-h-60 overflow-auto scroll-container"
                    >
                        {loading && filtered.length === 0 ? (
                            <div style={{ padding: '16px' }} className="px-4 py-8 flex items-center justify-center">
                                <span className="loader"></span>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div style={{ padding: '16px' }} className="px-4 py-8 text-center text-gray-500">
                                <p>No items found</p>
                            </div>
                        ) : (
                            <>
                                {filtered.map((item, index) => (
                                    <div
                                        key={index}
                                        ref={(el) => (itemRefs.current[index] = el)}
                                        style={{
                                            fontSize: "14px",
                                            fontWeight: 500,
                                            color: "#4A5353"
                                        }}
                                        className={`px-4 py-2 cursor-pointer font-inter ${highlightedIndex === index ? 'bg-[#E7F1F1]' : ''
                                            }`}
                                        onMouseDown={() => {
                                            selectedItemRef.current = item;
                                            hasUserInteractedRef.current = false;
                                            onSelect(item);
                                            setValue(labelExtractor(item));
                                            close(refKey);
                                        }}
                                    >
                                        {labelExtractor(item)}
                                    </div>
                                ))}

                                {hasMore && (
                                    <div
                                        ref={(el) => {
                                            setLoaderRef(el);
                                            loaderElementRef.current = el;
                                        }}
                                        style={{ height: "1px" }}
                                    >
                                        {loading && (
                                            <div className="w-full py-2 flex items-center justify-center">
                                                <span className="loader"></span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
            {error && <p className="error">{error}</p>}
        </>
    );
}